<script setup lang="ts">
/**
 * UI-kit component
 * Category: Tutors
 * Name: TutorProfile
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=3398-22644
 *
 * The identity block of a tutor card: photo (with the designed placeholder when empty),
 * name, the experience/grades meta rows, then education and subjects. Places TutorRating
 * over the photo's lower-left corner with an overhang (the photo box does not clip) and
 * owns its visibility — an empty rating hides the badge.
 * The labels Experience/Grades/Education/Subjects are fixed UI text, not props.
 * Text limits are LINE COUNTS (never fixed/max heights): name 1, experience 1, grades 1,
 * education 3, subjects 2. FILL horizontally, hugs vertically — it must NOT absorb the
 * card slack (that is TutorQuote's job).
 * Local implementation of the Figma DS component missing from @brighterly/lib-lumee-ui (dsGap);
 * composes the TutorPhoto + TutorAvatar pair (GRWT-510).
 */
// types
import type { TutorProfileProps } from '../types/interfaces'
// components
import { Icon } from '@brighterly/lib-lumee-ui'
import { TutorAvatar } from '@UI/Tutors/TutorAvatar'
import { TutorPhoto } from '@UI/Tutors/TutorPhoto'
import { TutorRating } from '@UI/Tutors/TutorRating'

defineOptions({
  name: 'TutorProfile',
})

withDefaults(defineProps<TutorProfileProps>(), {
  avatar: undefined,
  rating: '',
})
</script>

<template>
  <div class="tutor-profile">
    <div class="tutor-profile__identity">
      <span class="tutor-profile__photo">
        <TutorPhoto size="auto">
          <TutorAvatar
            v-if="avatar"
            :name="avatar"
            :alt="name"
          />
        </TutorPhoto>
        <TutorRating
          v-if="rating"
          class="tutor-profile__rating"
          :rating="rating"
        />
      </span>
      <div class="tutor-profile__info">
        <h3 class="tutor-profile__name btly-text-title-l lg:btly-text-title-xl">{{ name }}</h3>
        <ul class="tutor-profile__meta">
          <li class="tutor-profile__meta-row">
            <Icon
              name="graduation-cap"
              size="xs"
              weight="solid"
            />
            <span class="tutor-profile__meta-label btly-text-title-s">Experience:</span>
            <span class="tutor-profile__meta-value btly-text-body-m">{{ experience }}</span>
          </li>
          <li class="tutor-profile__meta-row">
            <Icon
              name="book"
              size="xs"
              weight="solid"
            />
            <span class="tutor-profile__meta-label btly-text-title-s">Grades:</span>
            <span class="tutor-profile__meta-value btly-text-body-m">{{ grades }}</span>
          </li>
        </ul>
      </div>
    </div>
    <div class="tutor-profile__credentials">
      <div class="tutor-profile__credential">
        <h4 class="tutor-profile__label btly-text-title-m">Education:</h4>
        <p class="tutor-profile__value tutor-profile__value--education btly-text-body-m lg:btly-text-body-l">{{ education }}</p>
      </div>
      <div class="tutor-profile__credential">
        <h4 class="tutor-profile__label btly-text-title-m">Subjects:</h4>
        <p class="tutor-profile__value tutor-profile__value--subjects btly-text-body-m lg:btly-text-body-l">{{ subjects }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_tutor-profile.scss" />
